import axios, { type AxiosError, type AxiosResponse } from 'axios';
import { ApiClient } from '~/api/generated';

export const useApiStore = defineStore('api', {
  state: () => {
    const authStore = useAuthStore();
    const config = useRuntimeConfig();

    axios.interceptors.response.use(
      (res: AxiosResponse) => res,
      async (error: AxiosError) => {
        if ([401, 403].includes(error.response?.status as number)) {
          // If the request is a request to refresh tokens
          if (error.response?.config?.url?.includes('/auth/token/refresh/')) {
            await authStore.logout();
          }

          // If the request is not a request to login user
          else if (!error.response?.config?.url?.includes('/auth/token/')) {
            try {
              await authStore.refreshTokens();

              error.config?.headers.setAuthorization(`Bearer ${authStore.access}`);

              return axios.request(error.config!);
            } catch (e) {
              await authStore.logout();
            }
          }
        }

        return Promise.reject(error);
      }
    );

    return {
      availableApiBases: [config.public.apiBase, config.public.apiBasePhoenix],
      apiBase: config.public.apiBase,
      wsBase: config.public.wsBase,
      api: new ApiClient({ BASE: config.public.apiBase })
    };
  },
  getters: {
    isValidApiBase(): boolean {
      return this.availableApiBases.includes(this.apiBase);
    }
  },
  actions: {
    $reset() {
      this.api = new ApiClient({ BASE: this.apiBase });
    },
    setApiBase(apiBase: string, wsBase: string) {
      this.apiBase = apiBase;
      this.wsBase = wsBase;
      this.api.request.config.BASE = apiBase;
    },
    setToken(token: string) {
      this.api.request.config.TOKEN = token;
    },
    validateAndResetApiBase() {
      const config = useRuntimeConfig();

      if (!this.isValidApiBase) {
        this.setApiBase(config.public.apiBase, config.public.wsBase);
      }
    }
  },
  persist: {
    storage: persistedState.localStorage
  }
});
